<template>
    <v-form ref="form" v-model="valid" data-cy="client-form">
        <v-row>
            <v-col cols="12" md="9">
                <v-row>
                    <v-col cols="12" md="3">
                        <h4 class="text-h6 text--secondary">
                            {{ $t('personal_information') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" :sm="isShowMiddleName ? 3 : 4">
                                <v-select
                                    v-model="person.prefix"
                                    :items="['Mr', 'Ms', 'Mrs']"
                                    :label="$t('salutation')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="prefix"
                                />
                            </v-col>
                            <v-col cols="12" :sm="isShowMiddleName ? 3 : 4">
                                <v-text-field
                                    v-model="person.firstName"
                                    :label="`${$t('first_name')}*`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[requiredRule('first_name')]"
                                    data-cy="first-name"
                                />
                            </v-col>
                            <v-col v-if="isShowMiddleName" cols="12" sm="3">
                                <v-text-field
                                    v-model="person.middleName"
                                    :label="$t('middle_name')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="middle-name"
                                />
                            </v-col>
                            <v-col cols="12" :sm="isShowMiddleName ? 3 : 4">
                                <v-text-field
                                    v-model="person.lastName"
                                    :label="`${$t('last_name')}*`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[requiredRule('last_name')]"
                                    data-cy="last-name"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="person.email"
                                    :label="`${$t('email')}*`"
                                    type="email"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[requiredRule('email'), emailRule]"
                                    data-cy="email"
                                    style="opacity: 1 !important"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="person.phone"
                                    :label="$t('phone')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[phoneSpaceRule()]"
                                    data-cy="phone"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="person.dateOfBirth"
                                    :label="$t('date_of_birth')"
                                    type="date"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="date-of-birth"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="person.gender"
                                    :items="genderOptions"
                                    :label="$t('gender')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="gender"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="!hideCitizenship" dense>
                            <v-col cols="12" sm="6">
                                <v-autocomplete
                                    v-model="person.citizenship"
                                    :items="countries"
                                    item-title="name"
                                    item-value="id"
                                    :label="$t('citizenship')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="citizenship"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3">
                        <h4 class="text-h6 text--secondary">
                            {{ $t('address') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" sm="8">
                                <v-text-field
                                    v-model="person.address"
                                    :label="$t('address')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="address"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.addressNumber"
                                    :label="$t('address_number')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="address-number"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.postalCode"
                                    :label="$t('postal_code')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="postal-code"
                                />
                            </v-col>
                            <v-col cols="12" sm="8">
                                <v-autocomplete
                                    v-model="person.cityId"
                                    :items="cities"
                                    item-title="name"
                                    item-value="id"
                                    :label="$t('city')"
                                    :loading="isLoadingData"
                                    :disabled="isLoadingData"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="city-id"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12">
                                <v-autocomplete
                                    v-model="person.countryCode"
                                    :items="countries"
                                    item-title="name"
                                    item-value="id"
                                    :label="$t('country')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="country-code"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row v-if="isMember">
                    <v-col cols="12" md="3">
                        <h4 class="text-h6 text--secondary">
                            {{ $t('membership') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12">
                                <v-autocomplete
                                    v-model="person.membershipLevelId"
                                    :items="membershipLevels"
                                    item-title="name"
                                    item-value="membershipLevelId"
                                    :label="$t('membership_level')"
                                    :loading="isLoadingData"
                                    :disabled="isLoadingData"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="membership-level-id"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row v-if="!isMember">
                    <v-col cols="12" md="3">
                        <h4 class="text-h6 text--secondary">
                            {{ $t('additional_information') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="person.disability"
                                    :label="$t('disability')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="disability"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="person.allergy"
                                    :label="$t('allergy')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="allergy"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12">
                                <v-combobox
                                    v-model="labels"
                                    :label="$t('labels')"
                                    multiple
                                    chips
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="labels"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3" class="text-h6 text--secondary">
                        <h4 class="text-h6 text--secondary">
                            {{ $t('comment') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <div class="px-0 font-italic text-subtitle-2">
                            {{ $t('enter_comment') }}
                        </div>
                        <v-textarea
                            v-model="person.comments"
                            variant="outlined"
                            color="primary"
                            data-cy="person-comments"
                        />
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="3" class="order-first order-md-last">
                <v-row v-if="person.personId" dense>
                    <div v-if="person.personId" class="text-subtitle-2 text-grey font-weight-regular ml-auto px-4">
                        {{ `${$t('id')}: ${person.personId}` }}
                    </div>
                </v-row>
                <v-row v-if="person.personId" dense class="px-4">
                    <v-switch
                        v-model="person.active"
                        color="primary"
                        class="ml-auto"
                        density="compact"
                        hide-details
                        :label="person.active ? $t('active') : $t('inactive')"
                        data-cy="person-active"
                    />
                </v-row>

                <h4 class="text-h6 text--secondary py-3">
                    {{ person.personId ? $t('change_password') : $t('password') }}
                </h4>
                <v-row dense>
                    <v-col>
                        <v-text-field
                            v-model="password"
                            :label="`${$t('password')}${
                                person.personId || isMember || isTourOperator || isAgency ? '' : '*'
                            }`"
                            type="password"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            :rules="[
                                v =>
                                    !!v ||
                                    !!person.personId ||
                                    isMember ||
                                    isTourOperator ||
                                    isAgency ||
                                    `${$t('password')} ${$t('validation.required')}`,
                            ]"
                            data-cy="password"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <v-text-field
                            v-model="passwordToMatch"
                            :label="`${$t('password_confirm')}${
                                person.personId || isMember || isTourOperator || isAgency ? '' : '*'
                            }`"
                            type="password"
                            autocomplete="new-password"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            :rules="[
                                v =>
                                    password === v ||
                                    !password ||
                                    `${$t('password_confirm')} ${$t('validation.match')}`,
                            ]"
                            data-cy="password-confirm"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <v-autocomplete
                            v-model="person.b2CId"
                            :item-title="getB2CName"
                            item-value="b2cId"
                            :items="b2cs"
                            :label="'B2C'"
                            :loading="isLoadingData"
                            :disabled="isLoadingData"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="b2c-id"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <v-autocomplete
                            v-model="person.managerId"
                            item-title="name"
                            item-value="personId"
                            :items="users"
                            :label="$t('manager')"
                            :loading="isLoadingData"
                            :disabled="isLoadingData"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="manager-id"
                        />
                    </v-col>
                </v-row>
                <v-row v-if="isMember" dense>
                    <v-col>
                        <v-text-field
                            v-model="person.membershipNumber"
                            :label="$t('membership_number')"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="membership-number"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row class="px-4 py-4">
            <v-btn
                :loading="isLoading"
                :disabled="isLoading || !valid"
                color="primary"
                class="mr-4"
                data-cy="save"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
            <!-- Hidden: Reservations and Communications buttons removed from client edit page -->
            <!--
            <v-btn
                v-if="person.personId && !isMember"
                :disabled="isLoading"
                variant="outlined"
                color="primary"
                class="mr-4"
                data-cy="go-to-orders"
                @click="goTo('orders')"
            >
                {{ $t('orders') }}
            </v-btn>
            <v-btn
                v-if="person.personId && !isMember"
                :disabled="isLoading"
                variant="outlined"
                color="primary"
                data-cy="go-to-communications"
                @click="goTo('communications')"
            >
                {{ $t('communications') }}
            </v-btn>
            -->
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, Watch, toNative} from 'vue-facing-decorator'
    import __ClientForm, {ClientForm as _ClientForm} from '~src/components/account/forms/ClientForm.vue'
    import {clean} from '~/utils/helpers'

    @Component({mixins: [__ClientForm]})
    export class ClientForm extends _ClientForm {
        // Eco Elite functionality moved to separate EcoElite component

        phoneSpaceRule() {
            return (v: string) =>
                !v || /\S\s+\S/.test(v) || (this.$t('validation.phone_space_required') as string)
        }

        async mounted() {
            await super.mounted()

            // Map core field names to ZSH field names for display
            if (this.person.birthdate && !this.person.dateOfBirth) {
                this.person.dateOfBirth = this.person.birthdate
            }
            if (this.person.addressLine && !this.person.address) {
                this.person.address = this.person.addressLine
            }
            if (this.person.contactPhone && !this.person.phone) {
                this.person.phone = this.person.contactPhone
            }
            if (this.person.citizenshipId && !this.person.citizenship) {
                this.person.citizenship = this.person.citizenshipId
            }
            if (this.person.countryId && !this.person.countryCode) {
                this.person.countryCode = this.person.countryId
            }
        }

        async save() {
            // Map ZSH field names to core field names before saving
            const originalDateOfBirth = this.person.dateOfBirth
            const originalAddress = this.person.address
            const originalPhone = this.person.phone
            const originalCitizenship = this.person.citizenship
            const originalCountryCode = this.person.countryCode

            try {
                // Map ZSH field names to core field names
                if (this.person.dateOfBirth) {
                    this.person.birthdate = this.person.dateOfBirth
                }
                if (this.person.address) {
                    this.person.addressLine = this.person.address
                }
                if (this.person.phone) {
                    this.person.contactPhone = this.person.phone
                }
                if (this.person.citizenship) {
                    this.person.citizenshipId = this.person.citizenship
                }
                if (this.person.countryCode) {
                    this.person.countryId = this.person.countryCode
                }

                // Call the parent save method
                await super.save()
            } finally {
                // Restore original field names to maintain form state
                this.person.dateOfBirth = originalDateOfBirth
                this.person.address = originalAddress
                this.person.phone = originalPhone
                this.person.citizenship = originalCitizenship
                this.person.countryCode = originalCountryCode
            }
        }
    }

    export default {...toNative(ClientForm), ssrRender: __ClientForm.ssrRender}
</script>
